import { Map } from 'immutable';

import { getDeletedCombinations, updateCombinations } from '@/utils/variations/combinations';
import { getRetainFromVariations } from '@/utils/variations/retain';
import { updateVariationsErrors } from '@/utils/validations/variations';
import { getOptionFormatter } from '@/utils/taxonomy';
import { updateInfluence } from '@/utils/variations/influence';
import { newVariations } from '@/utils/product/new';
import { setInvalid } from '@/utils/variations/errors';
import { getSize } from '@/utils/iterable/getSize';

import { COMBINATIONS, NUMBER_OF_VARIANTS, INFLUENCE, VARIANTS, ALL, RETAIN, DELETED } from '@/constants/variations';
import { ATTRIBUTE_DEFAULTS, CURRENCY, ERROR, REQUIRED, VALUE } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { CUSTOM_PROPERTY_IDS } from '@/constants/taxonomy';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

function filterCombinationsWithEmptyValue(path) {
  return function filterCombinations(combination) {
    if (typeof path === 'string') return !!combination.get(path);

    if (Array.isArray(path)) return !!combination.getIn(path);

    return false;
  };
}

function convertEbayVariationsToEtsy({ product, shop, shopData, validate = true, value }) {
  let variations = value;

  const influences = {
    [ATTRIBUTE.PRICE]: (
      !variations.getIn([RETAIN, ATTRIBUTE.PRICE]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.PRICE))
    ),
    [ATTRIBUTE.QUANTITY]: (
      !variations.getIn([RETAIN, ATTRIBUTE.QUANTITY]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.QUANTITY))
    ),
    [ATTRIBUTE.SKU]: (
      !variations.getIn([RETAIN, ATTRIBUTE.SKU]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.SKU))
    ),
  };

  function reduceVariants(result, variant, variantIndex) {
    if (variantIndex >= NUMBER_OF_VARIANTS[ETSY]) return result;

    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
        valueId: null,
      });
    }

    result.influence = result.influence.set(variantIndex, true);
    result.variants = result.variants.push(
      Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        property: CUSTOM_PROPERTY_IDS[ETSY][variantIndex],
        options: variant.get('options').map(mapOptions),
      })
    );

    return result;
  }

  const { influence, variants } = variations
    .get(VARIANTS)
    .slice(0, NUMBER_OF_VARIANTS[ETSY])
    .reduce(reduceVariants, { influence: EMPTY.MAP, variants: EMPTY.LIST });

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      function findOption(variationOption) {
        return variationOption.get('value') === option.get('name');
      }

      return Map({
        name: option.get('name'),
        optionId: variants.getIn([optionIndex, 'options']).find(findOption).get('id'),
        variantId: variants.getIn([optionIndex, 'id']),
      });
    }

    const price = influences[ATTRIBUTE.PRICE]
      ? combination.get(ATTRIBUTE.PRICE)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE], '');

    const quantity = influences[ATTRIBUTE.QUANTITY]
      ? combination.get(ATTRIBUTE.QUANTITY)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], '');

    const sku = influences[ATTRIBUTE.SKU]
      ? combination.get(ATTRIBUTE.SKU)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], '');

    return Map({
      id: '',
      options: combination.get('options').slice(0, NUMBER_OF_VARIANTS[ETSY]).map(mapOptions),
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.VISIBILITY]: true,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: ETSY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(REQUIRED)
      .set(INFLUENCE,
        Map({
          [ATTRIBUTE.PRICE]: influences[ATTRIBUTE.PRICE] ? influence : EMPTY.MAP,
          [ATTRIBUTE.QUANTITY]: influences[ATTRIBUTE.QUANTITY] ? influence : EMPTY.MAP,
          [ATTRIBUTE.SKU]: influences[ATTRIBUTE.SKU] ? influence : EMPTY.MAP,
        })
      )
      .set(VARIANTS, variants)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: ETSY,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: ETSY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
        channel: ETSY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertEbayVariationsToFaire({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const variants = variations.get(VARIANTS);
  const empty = variations.has(RETAIN) ? null : '';
  const emptyPrice = Map({
    [CURRENCY.AUD]: empty,
    [CURRENCY.CAD]: empty,
    [CURRENCY.EUR]: empty,
    [CURRENCY.GBP]: empty,
    [CURRENCY.USD]: empty,
  });

  function mapVariants(variant, variantIndex) {
    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
      });
    }

    return Map({
      id: -1 - variantIndex,
      name: variant.get('name'),
      options: variant.get('options').map(mapOptions),
    });
  }

  function mapCombinations(combination) {
    const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
      ? null
      : combination.get(ATTRIBUTE.PRICE);

    const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
      ? null
      : combination.get(ATTRIBUTE.QUANTITY);

    const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
      ? null
      : combination.get(ATTRIBUTE.SKU);

    return Map({
      id: '',
      options: combination.get('options').slice(0, NUMBER_OF_VARIANTS[FAIRE]),
      imageUrl: null,
      [ATTRIBUTE.DEADLINE_DATE]: null,
      [ATTRIBUTE.DISTANCE_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT],
      [ATTRIBUTE.END_DATE]: null,
      [ATTRIBUTE.GTIN]: empty,
      [ATTRIBUTE.HEIGHT]: empty,
      [ATTRIBUTE.LENGTH]: empty,
      [ATTRIBUTE.MASS_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT],
      [ATTRIBUTE.PREORDER]: false,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.RESTOCK_DATE]: null,
      [ATTRIBUTE.RETAIL_PRICE]: emptyPrice.set(CURRENCY.USD, price),
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.START_DATE]: null,
      [ATTRIBUTE.TARIFF_CODE]: empty,
      [ATTRIBUTE.WEIGHT]: empty,
      [ATTRIBUTE.WHOLESALE_PRICE]: emptyPrice,
      [ATTRIBUTE.WIDTH]: empty,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: FAIRE });
    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(REQUIRED)
      .set(INFLUENCE, EMPTY.MAP)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations))
      .set(VARIANTS, variants.slice(0, NUMBER_OF_VARIANTS[FAIRE]).map(mapVariants));

    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: FAIRE,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: FAIRE, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: FAIRE, shop, shopData, variations }))
    : variations;
}

function convertEbayVariationsToShopify({ product, shop, shopData, validate = true }) {
  let variations = product.get(FORM.VARIATIONS);
  const empty = variations.has(RETAIN) ? null : '';
  const variants = variations.get(VARIANTS);

  function mapVariants(variant, variantIndex) {
    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
      });
    }

    return Map({
      id: -1 - variantIndex,
      name: variant.get('name'),
      options: variant.get('options').map(mapOptions),
    });
  }

  function mapCombinations(combination) {
    const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
      ? null
      : combination.get(ATTRIBUTE.PRICE);

    const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
      ? null
      : combination.get(ATTRIBUTE.QUANTITY);

    const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
      ? null
      : combination.get(ATTRIBUTE.SKU);

    return Map({
      id: '',
      options: combination.get('options').slice(0, NUMBER_OF_VARIANTS[SHOPIFY]),
      imageId: null,
      imageHash: null,
      [ATTRIBUTE.BARCODE]: empty,
      [ATTRIBUTE.CAP]: empty,
      [ATTRIBUTE.CHARGE_TAX]: true,
      [ATTRIBUTE.CONTINUE_SELLING]: false,
      [ATTRIBUTE.COUNTRY_CODE]: empty,
      [ATTRIBUTE.CPI]: empty,
      [ATTRIBUTE.HS_CODE]: empty,
      [ATTRIBUTE.PHYSICAL]: true,
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.TRACK_QUANTITY]: true,
      [ATTRIBUTE.UNIT]: ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT],
      [ATTRIBUTE.WEIGHT]: empty,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: SHOPIFY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(REQUIRED)
      .delete(INFLUENCE)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations))
      .set(VARIANTS, variants.slice(0, NUMBER_OF_VARIANTS[SHOPIFY]).map(mapVariants));

    if (getSize(product.getIn([FORM.VARIATIONS, INFLUENCE]))) {
      variations = variations.set(DELETED, getDeletedCombinations({ variations }));
    }

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: SHOPIFY,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: SHOPIFY, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: SHOPIFY, shop, shopData, variations }))
    : variations;
}

function convertEtsyVariationsToEbay({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';

  function reduceVariantsToFormatters(result, variant, variantIndex) {
    result[variantIndex] = getOptionFormatter({ channel: ETSY, scale: variant.get('scale') });
    return result;
  }

  const formatters = variations.get(VARIANTS).reduce(reduceVariantsToFormatters, []);

  function updateVariantsForEbay(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function mapOptions(option = EMPTY.MAP, optionIndex) {
        const result = Map({
          id: -1 - optionIndex,
          value: formatters[variantIndex](option.get('value')),
        });

        return variations.has(RETAIN)
          ? result
          : result.set('photos', EMPTY.LIST);
      }

      return Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        options: variant.get('options').map(mapOptions),
      });
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForEbay(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP, combinationIndex) {
      function mapOptions(option, optionIndex) {

        return Map({
          name: formatters[optionIndex](option.get('name')),
          variantId: -1 - optionIndex,
        });
      }

      const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
        ? null
        : combination.get(ATTRIBUTE.PRICE);

      const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
        ? null
        : combination.get(ATTRIBUTE.QUANTITY);

      const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
        ? null
        : combination.get(ATTRIBUTE.SKU);

      return Map({
        id: -1 - combinationIndex,
        options: combination.get('options').map(mapOptions),
        [ATTRIBUTE.PRICE]: price || empty,
        [ATTRIBUTE.QUANTITY]: quantity || empty,
        [ATTRIBUTE.SKU]: sku || empty,
      });
    }

    return combinations.map(mapCombinations);
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: EBAY });
  } else {
    variations = variations
      .delete(ERROR)
      .set(INFLUENCE, EMPTY.MAP)
      .set(REQUIRED, newVariations({ channel: EBAY }).get(REQUIRED))
      .update(VARIANTS, updateVariantsForEbay)
      .update(COMBINATIONS, updateCombinationsForEbay);
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: EBAY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]),
        itemSpecifics: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
        channel: EBAY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertEtsyVariationsToFaire({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';
  const emptyPrice = Map({
    [CURRENCY.AUD]: empty,
    [CURRENCY.CAD]: empty,
    [CURRENCY.EUR]: empty,
    [CURRENCY.GBP]: empty,
    [CURRENCY.USD]: empty,
  });

  function reduceVariantsToFormatters(result, variant, variantIndex) {
    result[variantIndex] = getOptionFormatter({ channel: ETSY, scale: variant.get('scale') });
    return result;
  }

  const formatters = variations.get(VARIANTS).reduce(reduceVariantsToFormatters, []);

  function updateVariantsForFaire(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function mapOptions(option = EMPTY.MAP, optionIndex) {
        return Map({
          id: -1 - optionIndex,
          value: formatters[variantIndex](option.get('value')),
        });
      }

      return Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        options: variant.get('options').map(mapOptions),
      });
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForFaire(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP, combinationIndex) {
      function mapOptions(option, optionIndex) {
        return Map({
          name: formatters[optionIndex](option.get('name')),
          variantId: -1 - optionIndex,
        });
      }

      const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
        ? null
        : combination.get(ATTRIBUTE.PRICE);

      const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
        ? null
        : combination.get(ATTRIBUTE.QUANTITY);

      const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
        ? null
        : combination.get(ATTRIBUTE.SKU);

      return Map({
        id: -1 - combinationIndex,
        options: combination.get('options').map(mapOptions),
        imageUrl: null,
        [ATTRIBUTE.DEADLINE_DATE]: null,
        [ATTRIBUTE.DISTANCE_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT],
        [ATTRIBUTE.END_DATE]: null,
        [ATTRIBUTE.GTIN]: empty,
        [ATTRIBUTE.HEIGHT]: empty,
        [ATTRIBUTE.LENGTH]: empty,
        [ATTRIBUTE.MASS_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT],
        [ATTRIBUTE.PREORDER]: false,
        [ATTRIBUTE.QUANTITY]: quantity,
        [ATTRIBUTE.RESTOCK_DATE]: null,
        [ATTRIBUTE.RETAIL_PRICE]: emptyPrice.set(CURRENCY.USD, price),
        [ATTRIBUTE.SKU]: sku,
        [ATTRIBUTE.START_DATE]: null,
        [ATTRIBUTE.TARIFF_CODE]: empty,
        [ATTRIBUTE.WEIGHT]: empty,
        [ATTRIBUTE.WHOLESALE_PRICE]: emptyPrice,
        [ATTRIBUTE.WIDTH]: empty,
      });
    }

    return combinations.map(mapCombinations);
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: FAIRE });
    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });
  } else {
    variations = variations
      .delete(ERROR)
      .set(INFLUENCE, EMPTY.MAP)
      .update(VARIANTS, updateVariantsForFaire)
      .update(COMBINATIONS, updateCombinationsForFaire);

    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: FAIRE, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: FAIRE, shop, shopData, variations }))
    : variations;
}

function convertEtsyVariationsToShopify({ shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';
  const variants = variations.get(VARIANTS);

  function reduceVariants(result, variant, variantIndex) {
    result[variantIndex] = getOptionFormatter({ channel: ETSY, scale: variant.get('scale') });
    return result;
  }

  const formatters = variants.reduce(reduceVariants, []);

  function mapVariants(variant, variantIndex) {
    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: formatters[variantIndex](option.get('value')),
      });
    }

    return Map({
      id: -1 - variantIndex,
      name: variant.get('name'),
      options: variant.get('options').map(mapOptions),
    });
  }

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      return Map({
        name: formatters[optionIndex](option.get('name')),
        variantId: -1 - optionIndex,
      });
    }

    const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
      ? null
      : combination.get(ATTRIBUTE.PRICE);

    const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
      ? null
      : combination.get(ATTRIBUTE.QUANTITY);

    const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
      ? null
      : combination.get(ATTRIBUTE.SKU);

    return Map({
      id: '',
      options: combination.get('options').map(mapOptions),
      imageId: null,
      imageHash: null,
      [ATTRIBUTE.BARCODE]: empty,
      [ATTRIBUTE.CAP]: empty,
      [ATTRIBUTE.CHARGE_TAX]: true,
      [ATTRIBUTE.CONTINUE_SELLING]: false,
      [ATTRIBUTE.COUNTRY_CODE]: empty,
      [ATTRIBUTE.CPI]: empty,
      [ATTRIBUTE.HS_CODE]: empty,
      [ATTRIBUTE.PHYSICAL]: true,
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.TRACK_QUANTITY]: true,
      [ATTRIBUTE.UNIT]: ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT],
      [ATTRIBUTE.WEIGHT]: empty,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: SHOPIFY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(INFLUENCE)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations))
      .set(VARIANTS, variants.map(mapVariants));
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: SHOPIFY, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: SHOPIFY, shop, shopData, variations }))
    : variations;
}

function convertFaireVariationsToEbay({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';

  function updateVariantsForEbay(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function mapOptions(option = EMPTY.MAP, optionIndex) {
        const result = Map({
          id: -1 - optionIndex,
          value: option.get('value'),
        });

        return variations.has(RETAIN)
          ? result
          : result.set('photos', EMPTY.LIST);
      }

      return Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        options: variant.get('options').map(mapOptions),
      });
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForEbay(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP, combinationIndex) {
      function mapOptions(option, optionIndex) {
        return Map({
          name: option.get('name'),
          variantId: -1 - optionIndex,
        });
      }

      const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
        ? null
        : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD]);

      const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
        ? null
        : combination.get(ATTRIBUTE.QUANTITY);

      const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
        ? null
        : combination.get(ATTRIBUTE.SKU);

      return Map({
        id: -1 - combinationIndex,
        options: combination.get('options').map(mapOptions),
        [ATTRIBUTE.PRICE]: price || empty,
        [ATTRIBUTE.QUANTITY]: quantity || empty,
        [ATTRIBUTE.SKU]: sku || empty,
      });
    }

    return combinations.map(mapCombinations);
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: EBAY });
  } else {
    variations = variations
      .delete(ERROR)
      .set(INFLUENCE, EMPTY.MAP)
      .set(REQUIRED, newVariations({ channel: EBAY }).get(REQUIRED))
      .update(VARIANTS, updateVariantsForEbay)
      .update(COMBINATIONS, updateCombinationsForEbay);
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: EBAY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]),
        itemSpecifics: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
        channel: EBAY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertFaireVariationsToEtsy({ product, shop, shopData, validate = true, value }) {
  let variations = value;

  const influences = {
    [ATTRIBUTE.PRICE]: (
      !variations.getIn([RETAIN, ATTRIBUTE.PRICE]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD))
    ),
    [ATTRIBUTE.QUANTITY]: (
      !variations.getIn([RETAIN, ATTRIBUTE.QUANTITY]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.QUANTITY))
    ),
    [ATTRIBUTE.SKU]: (
      !variations.getIn([RETAIN, ATTRIBUTE.SKU]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.SKU))
    ),
  };

  function reduceVariants(result, variant, variantIndex) {
    if (variantIndex >= NUMBER_OF_VARIANTS[ETSY]) return result;

    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
        valueId: null,
      });
    }

    result.influence = result.influence.set(variantIndex, true);
    result.variants = result.variants.push(
      Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        property: CUSTOM_PROPERTY_IDS[ETSY][variantIndex],
        options: variant.get('options').map(mapOptions),
      })
    );

    return result;
  }

  const { influence, variants } = variations
    .get(VARIANTS)
    .slice(0, NUMBER_OF_VARIANTS[ETSY])
    .reduce(reduceVariants, { influence: EMPTY.MAP, variants: EMPTY.LIST });

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      function findOption(variationOption) {
        return variationOption.get('value') === option.get('name');
      }

      return Map({
        name: option.get('name'),
        optionId: variants.getIn([optionIndex, 'options']).find(findOption).get('id'),
        variantId: variants.getIn([optionIndex, 'id']),
      });
    }

    const price = influences[ATTRIBUTE.PRICE]
      ? combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD])
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE], '');

    const quantity = influences[ATTRIBUTE.QUANTITY]
      ? combination.get(ATTRIBUTE.QUANTITY)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], '');

    const sku = influences[ATTRIBUTE.SKU]
      ? combination.get(ATTRIBUTE.SKU)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], '');

    return Map({
      id: '',
      options: combination.get('options').slice(0, NUMBER_OF_VARIANTS[ETSY]).map(mapOptions),
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.VISIBILITY]: true,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: ETSY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(REQUIRED)
      .set(INFLUENCE,
        Map({
          [ATTRIBUTE.PRICE]: influences[ATTRIBUTE.PRICE] ? influence : EMPTY.MAP,
          [ATTRIBUTE.QUANTITY]: influences[ATTRIBUTE.QUANTITY] ? influence : EMPTY.MAP,
          [ATTRIBUTE.SKU]: influences[ATTRIBUTE.SKU] ? influence : EMPTY.MAP,
        })
      )
      .set(VARIANTS, variants)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: ETSY,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: ETSY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
        channel: ETSY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertFaireVariationsToShopify({ shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';
  const variants = variations.get(VARIANTS);

  function mapVariants(variant, variantIndex) {
    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
      });
    }

    return Map({
      id: -1 - variantIndex,
      name: variant.get('name'),
      options: variant.get('options').map(mapOptions),
    });
  }

  function mapCombinations(combination) {
    const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
      ? null
      : combination.getIn([ATTRIBUTE.RETAIL_PRICE, CURRENCY.USD]);

    const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
      ? null
      : combination.get(ATTRIBUTE.QUANTITY);

    const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
      ? null
      : combination.get(ATTRIBUTE.SKU);

    const weight = variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])
      ? null
      : combination.get(ATTRIBUTE.WEIGHT);

    const massUnit = variations.getIn([RETAIN, ATTRIBUTE.SHIPPING])
      ? ATTRIBUTE_DEFAULTS[SHOPIFY][ATTRIBUTE.UNIT]
      : combination.get(ATTRIBUTE.MASS_UNIT);

    const continueSelling = variations.getIn([INFLUENCE, ATTRIBUTE.CONTINUE_SELLING]);

    return Map({
      id: '',
      options: combination.get('options'),
      imageId: null,
      imageHash: null,
      [ATTRIBUTE.BARCODE]: empty,
      [ATTRIBUTE.CAP]: empty,
      [ATTRIBUTE.CHARGE_TAX]: true,
      [ATTRIBUTE.CONTINUE_SELLING]: continueSelling,
      [ATTRIBUTE.COUNTRY_CODE]: empty,
      [ATTRIBUTE.CPI]: empty,
      [ATTRIBUTE.HS_CODE]: empty,
      [ATTRIBUTE.PHYSICAL]: true,
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.TRACK_QUANTITY]: !variations.getIn([RETAIN, ATTRIBUTE.QUANTITY]),
      [ATTRIBUTE.UNIT]: massUnit,
      [ATTRIBUTE.WEIGHT]: weight,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: SHOPIFY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(INFLUENCE)
      .delete(REQUIRED)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations))
      .set(VARIANTS, variants.map(mapVariants));
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: SHOPIFY, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: SHOPIFY, shop, shopData, variations }))
    : variations;
}

function convertShopifyVariationsToEbay({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';

  function updateVariantsForEbay(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function mapOptions(option = EMPTY.MAP, optionIndex) {
        const result = Map({
          id: -1 - optionIndex,
          value: option.get('value'),
        });

        return variations.has(RETAIN)
          ? result
          : result.set('photos', EMPTY.LIST);
      }

      return Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        options: variant.get('options').map(mapOptions),
      });
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForEbay(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP, combinationIndex) {
      function mapOptions(option, optionIndex) {
        return Map({
          name: option.get('name'),
          variantId: -1 - optionIndex,
        });
      }

      const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
        ? null
        : combination.get(ATTRIBUTE.PRICE);

      const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
        ? null
        : combination.get(ATTRIBUTE.QUANTITY);

      const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
        ? null
        : combination.get(ATTRIBUTE.SKU);

      return Map({
        id: -1 - combinationIndex,
        options: combination.get('options').map(mapOptions),
        [ATTRIBUTE.PRICE]: price || empty,
        [ATTRIBUTE.QUANTITY]: quantity || empty,
        [ATTRIBUTE.SKU]: sku || empty,
      });
    }

    return combinations.map(mapCombinations);
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: EBAY });
  } else {
    variations = variations
      .delete(ERROR)
      .set(INFLUENCE, EMPTY.MAP)
      .set(REQUIRED, newVariations({ channel: EBAY }).get(REQUIRED))
      .update(VARIANTS, updateVariantsForEbay)
      .update(COMBINATIONS, updateCombinationsForEbay);
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: EBAY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]),
        itemSpecifics: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
        channel: EBAY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertShopifyVariationsToEtsy({ product, shop, shopData, validate = true, value }) {
  let variations = value;

  const influences = {
    [ATTRIBUTE.PRICE]: (
      !variations.getIn([RETAIN, ATTRIBUTE.PRICE]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.PRICE))
    ),
    [ATTRIBUTE.QUANTITY]: (
      !variations.getIn([RETAIN, ATTRIBUTE.QUANTITY]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.TRACK_QUANTITY))
    ),
    [ATTRIBUTE.SKU]: (
      !variations.getIn([RETAIN, ATTRIBUTE.SKU]) &&
      variations.get(COMBINATIONS).some(filterCombinationsWithEmptyValue(ATTRIBUTE.SKU))
    ),
  };

  function reduceVariants(result, variant, variantIndex) {
    if (variantIndex >= NUMBER_OF_VARIANTS[ETSY]) return result;

    function mapOptions(option, optionIndex) {
      return Map({
        id: -1 - optionIndex,
        value: option.get('value'),
        valueId: null,
      });
    }

    result.influence = result.influence.set(variantIndex, true);
    result.variants = result.variants.push(
      Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        property: CUSTOM_PROPERTY_IDS[ETSY][variantIndex],
        options: variant.get('options').map(mapOptions),
      })
    );

    return result;
  }

  const { influence, variants } = variations
    .get(VARIANTS)
    .slice(0, NUMBER_OF_VARIANTS[ETSY])
    .reduce(reduceVariants, { influence: EMPTY.MAP, variants: EMPTY.LIST });

  function mapCombinations(combination) {
    function mapOptions(option, optionIndex) {
      function findOption(variationOption) {
        return variationOption.get('value') === option.get('name');
      }

      return Map({
        name: option.get('name'),
        optionId: variants.getIn([optionIndex, 'options']).find(findOption).get('id'),
        variantId: variants.getIn([optionIndex, 'id']),
      });
    }

    const price = influences[ATTRIBUTE.PRICE]
      ? combination.get(ATTRIBUTE.PRICE)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.PRICE, VALUE], '');

    const quantity = influences[ATTRIBUTE.QUANTITY]
      ? combination.get(ATTRIBUTE.QUANTITY)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, VALUE], '');

    const sku = influences[ATTRIBUTE.SKU]
      ? combination.get(ATTRIBUTE.SKU)
      : variations.has(RETAIN)
        ? null
        : product.getIn([FORM.INVENTORY, ATTRIBUTE.SKU, VALUE], '');

    return Map({
      id: '',
      options: combination.get('options').slice(0, NUMBER_OF_VARIANTS[ETSY]).map(mapOptions),
      [ATTRIBUTE.PRICE]: price,
      [ATTRIBUTE.QUANTITY]: quantity,
      [ATTRIBUTE.SKU]: sku,
      [ATTRIBUTE.VISIBILITY]: true,
    });
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: ETSY });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(DELETED)
      .set(INFLUENCE,
        Map({
          [ALL]: getSize(variants) > 1 && (
            influences[ATTRIBUTE.PRICE] ||
            influences[ATTRIBUTE.QUANTITY] ||
            influences[ATTRIBUTE.SKU]
          ),
          [ATTRIBUTE.PRICE]: influences[ATTRIBUTE.PRICE] ? influence : EMPTY.MAP,
          [ATTRIBUTE.QUANTITY]: influences[ATTRIBUTE.QUANTITY] ? influence : EMPTY.MAP,
          [ATTRIBUTE.SKU]: influences[ATTRIBUTE.SKU] ? influence : EMPTY.MAP,
        })
      )
      .set(VARIANTS, variants)
      .set(COMBINATIONS, variations.get(COMBINATIONS).map(mapCombinations));

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: ETSY,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: ETSY, variations }));
  }

  return validate
    ? setInvalid(
      updateVariationsErrors({
        category: product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]),
        channel: ETSY,
        shop,
        shopData,
        variations,
      })
    )
    : variations;
}

function convertShopifyVariationsToFaire({ product, shop, shopData, validate = true, value }) {
  let variations = value;
  const empty = variations.has(RETAIN) ? null : '';
  const emptyPrice = Map({
    [CURRENCY.AUD]: empty,
    [CURRENCY.CAD]: empty,
    [CURRENCY.EUR]: empty,
    [CURRENCY.GBP]: empty,
    [CURRENCY.USD]: empty,
  });

  function updateVariantsForFaire(variants = EMPTY.LIST) {
    function mapVariants(variant = EMPTY.MAP, variantIndex) {
      function mapOptions(option = EMPTY.MAP, optionIndex) {
        return Map({
          id: -1 - optionIndex,
          value: option.get('value'),
        });
      }

      return Map({
        id: -1 - variantIndex,
        name: variant.get('name'),
        options: variant.get('options').map(mapOptions),
      });
    }

    return variants.map(mapVariants);
  }

  function updateCombinationsForFaire(combinations = EMPTY.LIST) {
    function mapCombinations(combination = EMPTY.MAP, combinationIndex) {
      function mapOptions(option, optionIndex) {
        return Map({
          name: option.get('name'),
          variantId: -1 - optionIndex,
        });
      }

      const price = variations.getIn([RETAIN, ATTRIBUTE.PRICE])
        ? null
        : combination.get(ATTRIBUTE.PRICE);

      const quantity = variations.getIn([RETAIN, ATTRIBUTE.QUANTITY])
        ? null
        : combination.get(ATTRIBUTE.QUANTITY);

      const sku = variations.getIn([RETAIN, ATTRIBUTE.SKU])
        ? null
        : combination.get(ATTRIBUTE.SKU);

      return Map({
        id: -1 - combinationIndex,
        options: combination.get('options').map(mapOptions),
        imageUrl: null,
        [ATTRIBUTE.DEADLINE_DATE]: null,
        [ATTRIBUTE.DISTANCE_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.DISTANCE_UNIT],
        [ATTRIBUTE.END_DATE]: null,
        [ATTRIBUTE.GTIN]: empty,
        [ATTRIBUTE.HEIGHT]: empty,
        [ATTRIBUTE.LENGTH]: empty,
        [ATTRIBUTE.MASS_UNIT]: ATTRIBUTE_DEFAULTS[FAIRE][ATTRIBUTE.MASS_UNIT],
        [ATTRIBUTE.PREORDER]: false,
        [ATTRIBUTE.QUANTITY]: quantity,
        [ATTRIBUTE.RESTOCK_DATE]: null,
        [ATTRIBUTE.RETAIL_PRICE]: emptyPrice.set(CURRENCY.USD, price),
        [ATTRIBUTE.SKU]: sku,
        [ATTRIBUTE.START_DATE]: null,
        [ATTRIBUTE.TARIFF_CODE]: empty,
        [ATTRIBUTE.WEIGHT]: empty,
        [ATTRIBUTE.WHOLESALE_PRICE]: emptyPrice,
        [ATTRIBUTE.WIDTH]: empty,
      });
    }

    return combinations.map(mapCombinations);
  }

  if (getSize(variations.get(COMBINATIONS)) === 0) {
    variations = newVariations({ channel: FAIRE });
    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });
  } else {
    variations = variations
      .delete(ERROR)
      .delete(DELETED)
      .set(INFLUENCE, EMPTY.MAP)
      .update(VARIANTS, updateVariantsForFaire)
      .update(COMBINATIONS, updateCombinationsForFaire);

    variations = updateInfluence({ channel: FAIRE, inventory: product.get(FORM.INVENTORY), variations });

    variations = variations.set(COMBINATIONS,
      updateCombinations({
        channel: FAIRE,
        inventory: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.INVENTORY),
        shipping: variations.has(RETAIN)
          ? undefined
          : product.get(FORM.SHIPPING),
        variants: variations.get(VARIANTS),
        variations,
      })
    );
  }

  if (variations.has(RETAIN)) {
    variations = variations.set(RETAIN, getRetainFromVariations({ channel: FAIRE, variations }));
  }

  return validate
    ? setInvalid(updateVariationsErrors({ channel: FAIRE, shop, shopData, variations }))
    : variations;
}

export function convertVariations({ from, to, value, ...rest }) {
  switch (from) {
    case EBAY: {
      switch (to) {
        case ETSY: {
          return convertEbayVariationsToEtsy({ value, ...rest });
        }

        case FAIRE: {
          return convertEbayVariationsToFaire({ value, ...rest });
        }

        case SHOPIFY: {
          return convertEbayVariationsToShopify({ value, ...rest });
        }

        default: {
          return value;
        }
      }
    }

    case ETSY: {
      switch (to) {
        case EBAY: {
          return convertEtsyVariationsToEbay({ value, ...rest });
        }

        case FAIRE: {
          return convertEtsyVariationsToFaire({ value, ...rest });
        }

        case SHOPIFY: {
          return convertEtsyVariationsToShopify({ value, ...rest });
        }

        default: {
          return value;
        }
      }
    }

    case FAIRE: {
      switch (to) {
        case EBAY: {
          return convertFaireVariationsToEbay({ value, ...rest });
        }

        case ETSY: {
          return convertFaireVariationsToEtsy({ value, ...rest });
        }

        case SHOPIFY: {
          return convertFaireVariationsToShopify({ value, ...rest });
        }

        default: {
          return value;
        }
      }
    }

    case SHOPIFY: {
      switch (to) {
        case EBAY: {
          return convertShopifyVariationsToEbay({ value, ...rest });
        }

        case ETSY: {
          return convertShopifyVariationsToEtsy({ value, ...rest });
        }

        case FAIRE: {
          return convertShopifyVariationsToFaire({ value, ...rest });
        }

        default: {
          return value;
        }
      }
    }

    default: {
      return value;
    }
  }
}
